/**
 * HC Dashboard Login Page
 * Admin-only authentication for HC Dashboard access
 */

import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Alert,
  Space,
} from 'antd';
import {
  LockOutlined,
  UserOutlined,
  HeartOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

// Auth API base — proxy through the same backend
const AUTH_API_URL = '/api/v1/auth/login';

interface LoginProps {
  onLoginSuccess: (token: string, user: { email: string; role: string }) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(AUTH_API_URL, {
        email: values.email,
        password: values.password,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': `req_${crypto.randomUUID()}`,
          'X-Client-Version': '2.0.0',
        },
        timeout: 15000,
      });

      const data = response.data;

      if (data.token || data.accessToken) {
        const token = data.token || data.accessToken;
        const user = data.user || { email: values.email, role: data.role || 'admin' };

        // Verify admin role
        const allowedRoles = ['super_admin', 'assistant_super_admin', 'admin'];
        if (user.role && !allowedRoles.includes(user.role)) {
          setError('Access denied. Admin role required for HC Dashboard.');
          setLoading(false);
          return;
        }

        onLoginSuccess(token, user);
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Invalid email or password.');
      } else if (err.response?.status === 429) {
        setError('Too many login attempts. Please wait and try again.');
      } else if (err.code === 'ECONNABORTED') {
        setError('Connection timed out. Please try again.');
      } else {
        setError(err.response?.data?.message || err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #001529 0%, #003a70 50%, #0050a0 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 420,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          borderRadius: 12,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <HeartOutlined
            style={{
              fontSize: 48,
              color: '#1890ff',
              marginBottom: 16,
              display: 'block',
            }}
          />
          <Title level={3} style={{ margin: 0 }}>
            HC Dashboard
          </Title>
          <Text type="secondary">
            Healing Collective - Admin Access
          </Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          name="hc-login"
          onFinish={handleLogin}
          layout="vertical"
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Admin email"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please enter your password' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{ height: 44 }}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center' }}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Admin, Super Admin, or Assistant Super Admin role required
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              GOLEGAL Healing Collective v2.0.0
            </Text>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default Login;
